import qs from 'qs';
import settings from '../settings.json';
import CookieUtils from '../lib/cookie-util';
import { trackSearchResultDisplay } from '../scripts/tracking/tracking';

/**
 * Get full Strapi URL from path
 * @param {string} path Path of the URL
 * @returns {string} Full Strapi URL
 */
export function getStrapiURL(path = '') {
  return `${process.env.NEXT_PUBLIC_STRAPI_API_URL || settings.apiUri}${path}`;
}

/**
 * Helper to make GET requests to Strapi API endpoints
 * @param {string} path Path of the API route
 * @param {Object} urlParamsObject URL params object, will be stringified
 * @param {Object} options Options passed to fetch
 * @returns Parsed API call response
 */
export async function fetchAPI(path, urlParamsObject = {}, options = {}) {
  // Merge default and user options
  const populateDeep = urlParamsObject.populateDeep === true ? true : false;

  if (urlParamsObject.populateDeep) {
    delete urlParamsObject.populateDeep;
  }

  const mergedOptions = {
    method: options?.body ? 'POST' : 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.apiAccessToken}`,
      "Strapi-Response-Format": "v4",
    },
    ...options,
  };

  if (mergedOptions.body) {
    mergedOptions.body = JSON.stringify(mergedOptions.body);
  }

  // Build request URL
  const queryString = mergedOptions.body
    ? qs.stringify({
      populate: '*',
    })
    : qs.stringify({
      ...{
        pagination: {
          limit: 25,
          start: 0,
        },
        status: urlParamsObject.filters?.slug ? 'draft' : 'published',
      },
      ...urlParamsObject,
    });
  const requestUrl = `${getStrapiURL(`/api${path}${queryString ? `?${queryString}` : ''}`)}${(populateDeep ? '&pLevel' : '')}`;

  if (settings.logFetches) {
    console.log(requestUrl);
  }

  // Retry transient failures. Node's global fetch (undici) reuses keep-alive
  // sockets that the upstream load balancer may have already closed, which
  // surfaces as ECONNRESET / "other side closed"; retrying simply grabs a fresh
  // socket. 5xx responses are retried too. Only safe (non-body) requests are
  // retried so writes are never repeated.
  const isWrite = !!mergedOptions.body;
  const maxAttempts = isWrite ? 1 : (settings.fetchRetries ?? 3);

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let response;
    try {
      response = await fetch(requestUrl, mergedOptions);
    } catch (e) {
      if (attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 150 * 2 ** (attempt - 1)));
        continue;
      }
      console.error(`fetchAPI: network error for ${path} after ${attempt} attempt(s): ${e?.message}`);
      return null;
    }

    if (response.ok) {
      return await response.json();
    }

    if (response.status >= 500 && attempt < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 150 * 2 ** (attempt - 1)));
      continue;
    }

    console.error(`fetchAPI: ${response.status} ${response.statusText} for ${path}`);
    return null;
  }

  return null;
}

export function assertUpstreamOk(results, context = '') {
  const failedIndex = results.findIndex((result) => result == null);
  if (failedIndex !== -1) {
    throw new Error(
      `fetchAPI upstream failure (request #${failedIndex}) in ${context}; keeping last good page instead of returning 404.`
    );
  }
}

export function getUserSearchResultSelection(searchTerm = null, searchData = null, onSelectCallback = null) {
  if (localStorage && CookieUtils.cookiesAccepted()) {
    try {
      searchTerm = searchTerm?.toLowerCase();
      const searchSelections = localStorage.getItem('searchSelections');
      const storedSearchSelections = searchSelections ? JSON.parse(localStorage.getItem('searchSelections')) : [];

      if (!searchTerm || storedSearchSelections.length === 0) {
        return storedSearchSelections;
      } else {
        return storedSearchSelections
          .filter((d) => d.description.toLowerCase().includes(searchTerm) || d.title.toLowerCase().includes(searchTerm) || !searchTerm?.length)
          .map((d) => {
            return {
              hit: d,
              text: d.title,
              type: 'history',
              onClick: (e) => {
                if (searchData && onSelectCallback != null) {
                  onSelectCallback(e, d, searchData);
                }
              },
            };
          });
      }
    } catch (e) {
      console.log(e);
    }
  }

  return [];
}

export function removeSavedUserSearchResultSelection(searchHit) {
  const results = getUserSearchResultSelection();
  if (results.length > 0 && searchHit && results.find((d) => d.id === searchHit.id)) {
    localStorage.setItem('searchSelections', JSON.stringify(results.filter((d) => d.id !== searchHit.id)));
    document.activeElement.blur();
  }
}

export function saveUserSearchResultSelection(searchHit) {
  if (localStorage && searchHit && CookieUtils.cookiesAccepted()) {
    searchHit.type = 'history';
    const storedSearchSelections = getUserSearchResultSelection();
    if (!storedSearchSelections.find((d) => d.id === searchHit.id)) {
      storedSearchSelections.push(searchHit);
      localStorage.setItem('searchSelections', JSON.stringify(storedSearchSelections));
    }
  }
}

export const SEARCH_LIMIT_MAX = 100;

export async function searchMeilisearch(searchTerm = '', resultLimit = 7) {
  const response = await fetch(`${settings.meilisearch.host}/indexes/article/search`, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${settings.meilisearch.apiKey}`,
    }),
    body: JSON.stringify({
      q: searchTerm,
      limit: Math.min(resultLimit, SEARCH_LIMIT_MAX),
      attributesToRetrieve: ['id', 'slug', 'title', 'synonyms', 'description', 'pageImpressionCount'],
      sort: ['title:desc', 'description:desc', 'synonyms:desc', 'blockContent.content:desc', 'pageImpressionCount:desc'],
      attributesToSearchOn: ['title', 'description', 'synonyms', 'blockContent.content'],
      showRankingScore: true,
    }),
  });

  if (!response.ok) {
    throw new Error('Search unavailable');
  }

  return response.json();
}

async function requestSearch(searchTerm, resultLimit) {
  if (typeof window === 'undefined') {
    return searchMeilisearch(searchTerm, resultLimit);
  }

  const response = await fetch('/api/search/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q: searchTerm, limit: resultLimit }),
  });

  if (!response.ok) {
    throw new Error('Search unavailable');
  }

  return response.json();
}

export async function fetchSearchAPI(searchTerm = '', onSelectCallback, resultLimit = 7, serverSideFetch = false, windowRef = null) {
  if (searchTerm?.length > 2 || resultLimit > 7) {
    const result = await requestSearch(searchTerm, resultLimit)
      .then((data) => {
        /*if (windowRef) {
          trackSearchResultDisplay(windowRef, data.query, data.estimatedTotalHits);
        }*/

        if (data.hits?.length > 0) {
          let result = [
            ...(serverSideFetch ? [] : getUserSearchResultSelection(searchTerm, data, onSelectCallback)),
            ...data.hits.map((r) => {
              return {
                hit: r,
                text: r.title,
                type: 'suggestion',
                onClick: (e) => {
                  onSelectCallback(e, r, data);
                },
              };
            }),
          ];

          return result;
        } else {
          return [];
        }
      })
      .catch((e) => {
        console.log(e);
        return [];
      });

    return result;
  }

  return new Promise((resolve) => {
    resolve([]);
  });
}
